import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Loader2, ShieldCheck, Undo2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { vendorPaymentsAPI } from '@/services/api';

const inr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

export const VoidPaymentDialog = ({ open, onOpenChange, payment, onDone }) => {
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await vendorPaymentsAPI.cancel(payment.id, reason, code);
      toast({ title: `Payment ${r.data.payment_number} voided`, description: `${inr(r.data.amount)} added back to the vendor's payable${r.data.released_grn_ids?.length ? ` · ${r.data.released_grn_ids.length} GRN(s) released for correction` : ''}` });
      setReason(''); setCode('');
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast({ title: 'Could not void payment', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="void-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Undo2 className="w-5 h-5 text-red-600" /> Void payment {payment?.payment_number || payment?.reference_number}</DialogTitle>
          <DialogDescription>
            The payment stays on record as cancelled with your reason. A reversal entry dated on the payment date adds {inr(payment?.amount)} back to the vendor's payable, and any GRN it was paid against can be corrected again.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Reason (min 5 chars)</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Recorded against the wrong vendor" data-testid="void-payment-reason" />
          </div>
          <div className="space-y-1">
            <Label className="flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> HQ security code</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Security code" data-testid="void-payment-code" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Keep payment</Button>
          <Button variant="destructive" disabled={busy || reason.trim().length < 5 || !code.trim()} onClick={submit} data-testid="void-payment-submit">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Void payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
